<i18n>
{
  "en": {
    "socialMedia": "Social media",
    "createSocialMediaError": "Error creating social media. Please, try again later.",
    "createSocialMediaSuccess": "Created social media \"{0}\" !",
    "editSocialMediaSuccess": "Social media \"{0}\" was successfully edited!",
    "editSocialMediaError": "Error editing social media \"{0}\". Please, try again later.",
    "deleteSocialMediaTitle": "Do you want to delete social media: \"{0}\" ?",
    "deleteSocialMediaDescription": "This can not be undone",
    "delete": "Delete",
    "deleteSocialMediaSuccess": "Social media \"{0}\" was deleted",
    "deleteSocialMediaError": "Error deleting social media \"{0}\". Please, try again later.",
    "add": "Add"
  },
  "es": {
    "socialMedia": "Redes sociales",
    "createSocialMediaError": "Error creando la red social. Inténtelo de nuevo más tarde.",
    "createSocialMediaSuccess": "Creado la red social \"{0}\" !",
    "editSocialMediaSuccess": "La red social \"{0}\" se editó correctamente",
    "editSocialMediaError": "Error editando la red social \"{0}\". Inténtelo de nuevo más tarde.",
    "deleteSocialMediaTitle": "¿Desea borrar la red social \"{0}\" ?",
    "deleteSocialMediaDescription": "Este cambio es irreversible",
    "delete": "Eliminar",
    "deleteSocialMediaSuccess": "La red social \"{0}\" fue borrado",
    "deleteSocialMediaError": "Error al borrar la red social \"{0}\". Inténtelo de nuevo más tarde.",
    "add":  "Añadir"
  }
}
</i18n>
<template>
  <div>
    <div class="d-flex flex-wrap mx-7 mt-3 mb-3">
      <v-row>
        <v-col cols="12" align-self="center" class="pa-0 my-4">
          <div class="text-center text-h5">
            {{ $t('socialMedia') }}
          </div>
        </v-col>
        <v-col align-self="end" class="pa-0 te">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon class="plus-icon mr-0" v-on="on" v-bind="attrs"
                     @click="openModal">
                <v-icon color="grey" large>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('add') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <v-divider/>
    <v-list>
      <div v-if="filteredSocialMediaList">
        <social-media-row
          v-for="socialMedia in filteredSocialMediaList" :key="socialMedia.guid"
          :socialMedia="socialMedia"
          @edit="onEdit"
          @delete="onDelete"
        >
        </social-media-row>
      </div>
    </v-list>
    <social-media-create-edit-modal
      :visible="createSocialMediaModal"
      @confirm="onConfirmSocialMedia"
      @cancel="onCloseModal"
      :socialMedia="socialMedia"
      type="create"
    />
    <social-media-create-edit-modal
      :visible="editSocialMediaModal"
      @confirm="onEditSocialMedia"
      @cancel="onCloseModal"
      :socialMedia="socialMedia"
      type="edit"
    />
    <dynameat-confirmation-modal :visible="deleteSocialMediaModal"
                                 :title="$t('deleteSocialMediaTitle', [socialMediaToDeleteTitle])"
                                 :description="$t('deleteSocialMediaDescription')"
                                 :confirm-text="$t('delete')" @confirm="onDeleteSocialMedia"
                                 @cancel="onCloseModal"/>
  </div>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import SocialMediaCreateEditModal from '@socialMedia/components/SocialMediaCreateEditModal';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import SocialMediaRow from '../components/socialMediaRow.vue';
import { SocialMedia } from '../models';

@Component
@Components({
  SocialMediaRow,
  SocialMediaCreateEditModal,
  DynameatConfirmationModal
})
@MapActions(['socialMediaListRequest', 'addNotification', 'createSocialMediaRequest', 'updateSocialMediaRequest', 'deleteSocialMediaRequest'])
@MapGetters(['socialMediaList'])
export default class SocialMediaList extends Vue {
  createSocialMediaModal = false;

  editSocialMediaModal = false;

  deleteSocialMediaModal = false;

  socialMedia = null;

  get filteredSocialMediaList() {
    return this.socialMediaList;
  }

  get socialMediaToDeleteTitle() {
    return this.socialMedia ? this.socialMedia.name : '';
  }

  async created() {
    await this.socialMediaListRequest();
  }

  openModal() {
    this.socialMedia = SocialMedia.toInstance();
    this.createSocialMediaModal = true;
  }

  onEdit(socialMedia) {
    const serialized = socialMedia.serialize();
    this.socialMedia = SocialMedia.toInstance(serialized);
    this.editSocialMediaModal = true;
  }

  onDelete(socialMedia) {
    const serialized = socialMedia.serialize();
    this.socialMedia = SocialMedia.toInstance(serialized);
    this.deleteSocialMediaModal = true;
  }

  onCloseModal() {
    this.createSocialMediaModal = false;
    this.editSocialMediaModal = false;
    this.deleteSocialMediaModal = false;
  }

  async onConfirmSocialMedia(socialMedia) {
    try {
      await this.createSocialMediaRequest(socialMedia);
      this.addNotification(createSuccessNotification(this.$t('createSocialMediaSuccess', [socialMedia.name]), null, 3000));
    } catch (e) {
      console.log(e);
      this.addNotification(createErrorNotification(this.$t('createSocialMediaError', [socialMedia.name])));
    } finally {
      this.onCloseModal();
      this.socialMedia = null;
      await this.socialMediaListRequest();
    }
  }

  async onDeleteSocialMedia() {
    try {
      await this.deleteSocialMediaRequest(this.socialMedia);
      this.addNotification(createSuccessNotification(this.$t('deleteSocialMediaSuccess', [this.socialMedia.name]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('deleteSocialMediaError', [this.socialMedia.name])));
    } finally {
      this.onCloseModal();
      this.socialMedia = null;
      await this.socialMediaListRequest();
    }
  }

  async onEditSocialMedia(socialMedia) {
    try {
      await this.updateSocialMediaRequest(socialMedia);
      this.addNotification(createSuccessNotification(this.$t('editSocialMediaSuccess', [socialMedia.name]), null, 3000));
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('editSocialMediaError', [socialMedia.name])));
    } finally {
      this.onCloseModal();
      this.socialMedia = null;
      await this.socialMediaListRequest();
    }
  }
}
</script>
